/**
 * Where the hero was standing, remembered across view rebuilds.
 *
 * The farm view is torn down and rebuilt whenever the player switches tabs (Shop,
 * Arena, Quests…) and on any reload. `ensurePlayer` had no memory, so it always
 * recomputed the same spawn just south of the farm — walk twenty tiles out to a
 * mining seam, glance at the Shop, come back, and you are standing at home again
 * with the walk to do over. That is the bug this closes.
 *
 * Client-side on purpose. The world is generated on the CLIENT from a seed and the
 * server has no map (see FieldService's comments), so it has no frame of reference
 * to store a position in and no way to validate one. Persisting server-side would
 * also mean a write on every step. localStorage is the honest home for it: it is
 * per-browser presentation state, and losing it costs one walk, never progress.
 *
 * Keyed by world SEED, so two accounts (or a re-rolled world) never inherit each
 * other's position.
 */

const KEY = 'tb-last-pos';
/** Anything older than this is stale — resume where you were, not where you were last week. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

export interface SavedPosition { x: number; y: number; seed: string; at: number }

/** Remember where the hero is. Cheap enough to call on a throttle from the frame loop. */
export function saveLastPosition(seed: string, x: number, y: number): void {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ x, y, seed, at: Date.now() } satisfies SavedPosition));
  } catch {
    // Private mode / quota. Losing the position is a minor annoyance, never an error
    // worth surfacing — the hero just starts at the farm as before.
  }
}

/**
 * The remembered position for this world, or null.
 *
 * Rejects a different seed and anything stale. Returns raw coordinates; the caller
 * still has to clamp them to the map and check the tile is not blocked, because a
 * building may have been placed where the player last stood.
 */
export function loadLastPosition(seed: string, now = Date.now()): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<SavedPosition>;
    if (!p || p.seed !== seed) return null;
    if (typeof p.x !== 'number' || typeof p.y !== 'number') return null;
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
    if (typeof p.at !== 'number' || now - p.at > MAX_AGE_MS) return null;
    return { x: p.x, y: p.y };
  } catch {
    return null;   // corrupt entry — fall back to the default spawn
  }
}

/** Forget it: on death (you respawn at the tent/home) and on sign-out. */
export function clearLastPosition(): void {
  try { localStorage.removeItem(KEY); } catch { /* nothing to do */ }
}
